import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Download, Key, ExternalLink } from "lucide-react";
import Button from "../components/ui/Button";

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/session-status?session_id=${sessionId}`,
      );
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error("Failed to check payment status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-foreground font-heading">{t("payment.verifying")}</p>
        </div>
      </div>
    );
  }

  const isPaid = status?.payment_status === "paid" || status?.status === "complete";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gold/20 mb-6 animate-float">
            <CheckCircle className="w-12 h-12 text-gold" />
          </div>
          <h1 className="font-display text-5xl text-gold mb-4" data-testid="payment-success-title">
            {t("payment.successTitle")}
          </h1>
          <p className="font-serif-fantasy italic text-lg text-parchment/90">
            {t("payment.successQuote")}
          </p>
        </div>

        <div className="rune-border bg-card/80 backdrop-blur rounded-xl p-8 mb-6">
          <h2 className="font-heading text-xl text-foreground mb-6">
            {t("payment.orderDetails")}
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between py-3 border-b border-border">
              <span className="text-muted-foreground">{t("payment.status")}</span>
              <span className={`font-heading ${isPaid ? "text-gold" : "text-muted-foreground"}`}>
                {isPaid ? t("payment.paid") : t("payment.processing")}
              </span>
            </div>

            {status?.customer_email && (
              <div className="flex justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">{t("payment.email")}</span>
                <span className="text-foreground">{status.customer_email}</span>
              </div>
            )}

            <div className="flex justify-between py-3">
              <span className="text-muted-foreground">{t("payment.sessionId")}</span>
              <span className="text-foreground text-sm font-mono">
                {sessionId?.substring(0, 20)}...
              </span>
            </div>
          </div>
        </div>

        {isPaid && (
          <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-8 mb-6">
            <h3 className="font-heading text-lg text-gold mb-4 flex items-center">
              <Key className="w-5 h-5 mr-2" />
              {t("payment.nextSteps")}
            </h3>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start">
                <span className="text-gold mr-3">1.</span>
                <span>{t("payment.step1")}</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3">2.</span>
                <span>{t("payment.step2")}</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3">3.</span>
                <span>{t("payment.step3")}</span>
              </li>
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/dashboard" className="flex-1">
            <Button variant="primary" size="lg" className="w-full">
              <Download className="w-4 h-4 mr-2" />
              {t("payment.goDashboard")}
            </Button>
          </Link>
          <Link to="/store" className="flex-1">
            <Button variant="secondary" size="lg" className="w-full">
              {t("payment.continueShopping")}
            </Button>
          </Link>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground mb-2">{t("payment.needHelp")}</p>
          <Link
            to="/contact"
            className="text-gold hover:text-gold/80 font-heading text-sm inline-flex items-center"
          >
            {t("payment.contactSupport")}
            <ExternalLink className="w-3 h-3 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
